import { useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import styles from '../ReassignDonationModal.module.scss'
import { useAddDonationToReassignmentQueueForm } from '../addToReassignmentQueueForm/useAddToReassignmentQueueForm/useAddDonationToReassignmentQueueForm'
import { AddDonationToReassignmentQueueForm } from '../addToReassignmentQueueForm/addToReassignmentQueueForm/addDonationToReassignmentQueueForm'
import { useOrganisationSearchBox } from '../addToReassignmentQueueForm/useOrganisationSearchBox/useOrganisationSearchBox'

import { AddToReassignmentQueueAllModalProps } from './AddToReassignmentQueueAllModal.types'
import { useMutation } from '@percent/admin-dashboard/common/hooks'
import { AcknowledgeModal, ActionModal, Alert, ConfirmationModal, Modal, Text } from '@percent/lemonade'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import { isApiError } from '@percent/admin-dashboard/common/utility/isApiError'

export function AddToReassignmentQueueAllModal({
  open,
  onClose,
  refresh,
  sourceOrganisationName,
  selectedOrganisation
}: AddToReassignmentQueueAllModalProps) {
  const { t } = useTranslation()
  const { donationsService } = useServices()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [selectedOrganisationName, setSelectedOrganisationName] = useState<string | null | undefined>(
    selectedOrganisation?.name
  )

  const [successDialogState, setSuccessDialogState] = useState(false)
  const [errorDialogState, setErrorDialogState] = useState(false)

  const [{ isLoading, error, data: bulkOperationResponse }, { apiFunc: addAllDonationsToReassignmentQueueApiFunc }] =
    useMutation(
      donationsService.addAllDonationsToReassignmentQueue,
      () => setSuccessDialogState(true),
      () => setErrorDialogState(true)
    )

  const { searchResults, query, isLoading: isOrganisationsLoading, setQuery } = useOrganisationSearchBox()

  const { formik, reasons, reasonOptions } = useAddDonationToReassignmentQueueForm({
    selectedOrganisation,
    onFormSubmit: async ({ organisationId, reassignmentKind, reason }) => {
      await addAllDonationsToReassignmentQueueApiFunc({
        organisationId: selectedOrganisation?.id ?? '',
        payload: {
          reason,
          reassignmentKind,
          newOrganisationId: organisationId
        }
      })
    }
  })

  const {
    touched,
    errors,
    setFieldTouched,
    setFieldValue,
    handleChange,
    handleBlur,
    setValues,
    handleSubmit,
    isValid,
    resetForm,
    values
  } = formik

  const handleCloseModal = () => {
    onClose()
    refresh()
    resetForm()
    setSelectedOrganisationName(null)
    setSuccessDialogState(false)
    setShowConfirmationModal(false)
  }

  const handleOnErrorClose = () => {
    setErrorDialogState(false)
  }

  const successOrErrorModal =
    successDialogState && !errorDialogState ? (
      <AcknowledgeModal
        result="positive"
        title={t('dialog.addAllToReassignmentQueue.success.queueTitle')}
        description={
          <div className={styles.errorPage}>
            <Text size="medium">
              <Trans
                i18nKey="dialog.addAllToReassignmentQueue.success.description"
                values={{
                  orgName: selectedOrganisationName,
                  eligibleDonations: bulkOperationResponse?.data?.data?.markedForReassignment
                }}
                components={{ b: <strong /> }}
              />
            </Text>
            {(!!bulkOperationResponse?.data?.data?.ineligibleDonations?.cancelled ||
              !!bulkOperationResponse?.data?.data?.ineligibleDonations?.disbursed ||
              !!bulkOperationResponse?.data?.data?.ineligibleDonations?.inActiveDisbursement) && (
              <div className={styles.errorDetails}>
                <ul>
                  {!!bulkOperationResponse?.data?.data?.ineligibleDonations?.cancelled && (
                    <li>
                      <Text size="small">
                        {t('dialog.addAllToReassignmentQueue.success.cancelledDonations', {
                          cancelledDonations: bulkOperationResponse?.data?.data?.ineligibleDonations?.cancelled
                        })}
                      </Text>
                    </li>
                  )}
                  {!!bulkOperationResponse?.data?.data?.ineligibleDonations?.disbursed && (
                    <li>
                      <Text size="small">
                        {t('dialog.addAllToReassignmentQueue.success.disbursedDonations', {
                          disbursedDonations: bulkOperationResponse?.data?.data?.ineligibleDonations?.disbursed
                        })}
                      </Text>
                    </li>
                  )}
                  {!!bulkOperationResponse?.data?.data?.ineligibleDonations?.inActiveDisbursement && (
                    <li>
                      <Text size="small">
                        {t('dialog.addAllToReassignmentQueue.success.inActiveDisbursementDonations', {
                          inActiveDisbursementDonations:
                            bulkOperationResponse?.data?.data?.ineligibleDonations?.inActiveDisbursement
                        })}
                      </Text>
                    </li>
                  )}
                </ul>
              </div>
            )}
          </div>
        }
        buttonText={t('button.close')}
        handleClose={handleCloseModal}
        viewTestId="success-donation-reassign-modal"
      />
    ) : (
      <AcknowledgeModal
        result="negative"
        title={t('dialog.addAllToReassignmentQueue.error.title')}
        buttonText={t('button.close')}
        handleClose={handleOnErrorClose}
        viewTestId="error-donation-reassign-modal"
      >
        <div className={styles.errorPage}>
          <Text size="medium">
            <Trans
              i18nKey="dialog.addAllToReassignmentQueue.error.description"
              values={{ orgB: selectedOrganisationName }}
              components={{ b: <strong /> }}
            />
          </Text>
          {error && isApiError(error) && (
            <div className={styles.errorDetails}>
              <Text size="small">{error.message}</Text>
              {error.reasons && (
                <ul>
                  {error.reasons.map((reason, i) => (
                    // eslint-disable-next-line react/no-array-index-key
                    <li key={`reason-${i}`}>
                      <Text size="small">{reason}</Text>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </AcknowledgeModal>
    )

  return (
    <Modal open={open} onClose={handleCloseModal} aria-labelledby="reassign-donations-form-modal">
      {!successDialogState && !errorDialogState ? (
        showConfirmationModal ? (
          <ConfirmationModal
            title={t('dialog.addAllToReassignmentQueue.confirm.title')}
            loading={isLoading}
            primaryButtonText={t('button.addToQueue')}
            primaryBtnTestId="confirm-donation-reassignment"
            secondaryButtonText={t('button.cancel')}
            handleSubmit={handleSubmit}
            handleClose={() => setShowConfirmationModal(false)}
          >
            {selectedOrganisationName ? (
              <Trans
                i18nKey="dialog.addAllToReassignmentQueue.confirm.description"
                values={{ orgA: sourceOrganisationName, orgB: selectedOrganisationName }}
                components={{ b: <strong /> }}
              />
            ) : (
              <Trans
                i18nKey="dialog.addAllToReassignmentQueue.confirm.description.withoutNewOrg"
                values={{ orgA: sourceOrganisationName }}
                components={{ b: <strong /> }}
              />
            )}
          </ConfirmationModal>
        ) : (
          <ActionModal
            title={t('dialog.addAllToReassignmentQueue.title')}
            primaryButtonText={t('button.addToQueue')}
            secondaryButtonText={t('button.cancel')}
            type="submit"
            variant={isValid ? 'primary' : 'secondary'}
            disabled={!(isValid && !isLoading)}
            loading={isLoading}
            handleClose={handleCloseModal}
            handleSubmit={() => setShowConfirmationModal(true)}
            primaryBtnTestId="reassign-donation-submit-button"
            secondaryBtnTestId="reassign-donation-cancel-button"
          >
            <div className={styles.withFieldGap}>
              <Alert variant="info" title={t('dialog.addAllToReassignmentQueue.alert.title')}>
                <Text size="small">
                  <Trans
                    i18nKey="dialog.addAllToReassignmentQueue.alert.description"
                    values={{ orgName: selectedOrganisationName }}
                    components={{ b: <strong /> }}
                  />
                </Text>
              </Alert>
            </div>
            <AddDonationToReassignmentQueueForm
              values={values}
              touched={touched}
              errors={errors}
              onOrganisationChange={(organisationId, organisationName) => {
                setValues({
                  ...values,
                  organisationId
                })
                setSelectedOrganisationName(organisationName)
              }}
              onQueryChange={q => {
                setQuery(q)
                setFieldTouched('organisationId')
              }}
              onReassignedReasonChange={value => {
                setFieldValue('reassignedReason', value)
              }}
              isOrganisationsQueryLoading={isOrganisationsLoading}
              query={query}
              searchResults={searchResults}
              reassignedReasonOptions={reasonOptions}
              onCustomReasonChange={handleChange}
              onCustomReasonBlur={handleBlur}
              reasons={reasons}
            />
          </ActionModal>
        )
      ) : (
        successOrErrorModal
      )}
    </Modal>
  )
}
